import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment/moment';
import '../pagination/pagination.css';
import '../../Assets/Style/style.css';
import Pagination from '../pagination/pagination';
import Select from 'react-select';
import LeaseApiService from '../../services/apiService';
import Modal from 'react-bootstrap/Modal';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { getRole } from '../../../Redux/selecters';
import CustomerMaster from './customerMaster';
import UpdateCustomerMaster from './editCustomer';
import { FiArrowDown, FiArrowUp, FiDownload, FiEdit2, FiEye, FiPlus, FiRotateCcw, FiSearch, FiTrash2 } from 'react-icons/fi';
import RangeChips from '../shared/rangeChips';
import { DEFAULT_RANGE, loadRange, paginate } from '../shared/dateRange';
import './customerListing.css';

// Customer list in the GRN list layout: clickable summary cards (they filter the list),
// search panel, table with sortable columns and icon actions, pager.

const emptyFilters = {
  fullName: '',
  contactid: '',
  contactNo: '',
  source: '',
  type: '',
  salesPerson: '',
  email: '',
  id: '',
  salecustomer: '',
  leaseCustomer: '',
  prospectlevel: '',
  leadStatus: '',
  nationality: null,
  country: null,
};

const selectProps = {
  className: 'p-0 w-100',
  menuPortalTarget: document.body,
  theme: (theme) => ({ ...theme, borderRadius: 5, padding: 9 }),
  styles: {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (provided, state) => ({ ...provided, backgroundColor: state.isFocused ? '#F5F5F5' : null, color: '#000' }),
  },
};

const dash = <span className="zero">—</span>;
const blank = (v) => v === null || v === undefined || v === '' || v === '0';
const dateText = (v) => {
  if (blank(v)) return '';
  const d = moment(v);
  return d.isValid() && d.year() > 1900 ? d.format('DD-MMM-YYYY') : '';
};
// The country code lives in Exp6 (confirmed on a saved record), the local number in mainContactNo/contactNo.
const mobileOf = (row) => {
  const code = row?.exp6 || row?.Exp6 || row?.exp5 || row?.Exp5 || '';
  const local = row?.mainContactNo || row?.MainContactno || row?.mainContactno || row?.contactNo || '';
  if (!local || local === '0') return '';
  return code && code !== '0' ? `${code}${local}` : local;
};

const AV_COLORS = [['#EDE7F8', '#4A2C86'], ['#E1EEF5', '#1F5F7A'], ['#E1F2E8', '#1E7A4F'], ['#FBF1D9', '#7A5300'], ['#FBE4E2', '#B3261E']];
const initialsOf = (n = '') => n.split(/\s+/).filter((w) => /[A-Za-z]/.test(w[0] || '')).slice(0, 2).map((w) => w[0].toUpperCase()).join('') || '?';

const COLUMNS = [
  ['customerNo', 'contactId'], ['customerName', 'name'], ['mobile', 'mobileNo'], ['emailId', 'email'], ['tradeLicNo', 'id'],
  ['country', 'country'], ['nationality', 'nationality'], ['city', 'city'], ['createdOn', 'createdOn'], ['createdBy', 'createdBy'], ['unitNo', 'project'],
];

const CustomerListing = () => {
  const { t } = useTranslation();
  const apiService = useMemo(() => new LeaseApiService(), []);
  const role = useSelector(getRole);
  const isAdmin = role === 'Admin' || role === 'admin';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(20);
  const [pagination, setPagination] = useState();
  const [counts, setCounts] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [previewModal, setPreviewModal] = useState(false);
  const [countriesList, setCountriesList] = useState([]);
  const [nationalityList, setNationalityList] = useState([]);
  const [customerTypeList, setCustomerTypeList] = useState([]);
  const [leadStatus, setLeadStatus] = useState([]);
  const [formModal, setFormModal] = useState({ mode: null, data: null });
  const [quick, setQuick] = useState('');
  const [sort, setSort] = useState({ key: '', dir: 1 });
  const [formData, setFormData] = useState(emptyFilters);
  // Period: last 7 / 15 / 30 / 90 days or all time (default 30 days), on the customer's created date.
  const [range, setRange] = useState(DEFAULT_RANGE);
  const rangeCache = useRef({ key: '', rows: [] });
  const countsCache = useRef({});

  const kind = useMemo(() => {
    const on = [formData.salecustomer && 'sale', formData.leaseCustomer && 'lease', formData.prospectlevel && 'prospect'].filter(Boolean);
    return on.length === 1 ? on[0] : 'all';
  }, [formData.salecustomer, formData.leaseCustomer, formData.prospectlevel]);

  const fetchDropdownData = async () => {
    try {
      const params = new URLSearchParams();
      params.append('dropdownTypes', 'CustomerType,Country,Nationality');
      const res = await apiService.getApiParamater('CUSTOMER_MULTI', `?${params.toString()}`);
      setCustomerTypeList(res?.data?.dropdowns?.CustomerType || []);
      setCountriesList(res?.data?.dropdowns?.Country || []);
      setNationalityList(res?.data?.dropdowns?.Nationality || []);
      setLeadStatus([
        { value: 'All', text: 'All' },
        { value: 'Open', text: 'Open' },
        { value: 'In Process', text: 'In Process' },
        { value: 'Closed', text: 'Closed' },
      ]);
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    }
  };

  const query = (pageNo, size, f) => {
    const params = new URLSearchParams();
    params.append('PageNo', pageNo || 1);
    params.append('PageSize', size || 20);
    params.append('FullName', f?.fullName || '');
    params.append('LeadStatus', f?.leadStatus?.label || '');
    params.append('ContactID', f?.contactid || '');
    params.append('CustomerType', f?.type?.label || '');
    params.append('Nationality', f?.nationality?.label || '');
    // api/Customer/Get has no Country parameter (checked against the Swagger schema), so country is filtered client-side.
    params.append('Email', f?.email || '');
    params.append('ContactNo', f?.contactNo || '');
    params.append('Source', f?.source?.label || '');
    params.append('SalesPerson', f?.salesPerson?.label || '');
    params.append('IsSaleCustomer', f?.salecustomer);
    params.append('IsLeaseCustomer', f?.leaseCustomer);
    params.append('IsProspectLevel', f?.prospectlevel);
    return params;
  };

  const getItems = async (newPageNo = pagination?.currentPage, override = {}) => {
    const { range: rangeOverride, ...filterOverride } = override;
    const f = { ...formData, ...filterOverride };
    const r = rangeOverride ?? range;
    const countryLabel = f?.country?.label;
    const byCountry = (list) => (countryLabel ? list.filter((row) => (row?.country || '').trim().toLowerCase() === countryLabel.trim().toLowerCase()) : list);
    try {
      setLoading(true);
      if (r === 'all') {
        const res = await apiService.getApiParamater('GET_CUSTOMER', `?${query(newPageNo, pageSize, f).toString()}`);
        setRows(byCountry(res?.data?.result || []));
        setPagination(res?.data?.paginationData);
        return;
      }
      // The customer API has no date filter: read pages (newest first), keep the ones inside the period, then page here.
      const key = `${r}|${query(1, 1, f).toString()}|${countryLabel || ''}`;
      let all = rangeCache.current.key === key ? rangeCache.current.rows : null;
      if (!all) {
        all = await loadRange({
          range: r,
          dateKey: 'createdOn',
          pageSize: 100,
          fetchPage: async (p, size) => byCountry((await apiService.getApiParamater('GET_CUSTOMER', `?${query(p, size, f).toString()}`))?.data?.result || []),
        });
        rangeCache.current = { key, rows: all };
      }
      const page = paginate(all, newPageNo || 1, pageSize || 20);
      setRows(page.list);
      setPagination(page.pagination);
    } catch (error) {
      console.error('Error fetching items:', error);
      toast.error(t('customerMaster.loadFailed', 'Unable to load customers.'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // Card numbers follow the chosen period. All time: one single-row request per kind (the API reports totalRecords).
  // A period: the customers created inside it are read (newest first, as the list does) and counted per kind.
  const countsFor = async (r) => {
    const inRange = async (flags) => {
      try {
        const rowsInRange = await loadRange({
          range: r,
          dateKey: 'createdOn',
          pageSize: 100,
          fetchPage: async (p, size) => (await apiService.getApiParamater('GET_CUSTOMER', `?${query(p, size, { ...emptyFilters, ...flags }).toString()}`))?.data?.result || [],
        });
        return rowsInRange.length;
      } catch (e) { return 0; }
    };
    const total = async (flags) => {
      try {
        const res = await apiService.getApiParamater('GET_CUSTOMER', `?${query(1, 1, { ...emptyFilters, ...flags }).toString()}`);
        return res?.data?.paginationData?.totalRecords ?? 0;
      } catch (e) { return 0; }
    };
    const one = r === 'all' ? total : inRange;
    const [all, sale, lease, prospect] = await Promise.all([
      one({}), one({ salecustomer: true }), one({ leaseCustomer: true }), one({ prospectlevel: true }),
    ]);
    return { all, sale, lease, prospect };
  };
  const loadCounts = async (r = range) => {
    const key = r;
    if (countsCache.current[key]) { setCounts(countsCache.current[key]); return; }
    const c = await countsFor(r);
    countsCache.current[key] = c;
    setCounts(c);
  };
  const refreshCounts = () => { countsCache.current = {}; return loadCounts(); };

  useEffect(() => {
    fetchDropdownData();
    getItems(1);
    loadCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickKind = (k) => {
    const next = kind === k ? 'all' : k;
    const flags = { salecustomer: next === 'sale' ? true : '', leaseCustomer: next === 'lease' ? true : '', prospectlevel: next === 'prospect' ? true : '' };
    setFormData((prev) => ({ ...prev, ...flags }));
    getItems(1, flags);
  };

  const handleClearFilters = () => {
    setFormData(emptyFilters);
    setQuick('');
    setRange(DEFAULT_RANGE);
    getItems(1, { ...emptyFilters, range: DEFAULT_RANGE });
    loadCounts(DEFAULT_RANGE);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  const setSelect = (name) => (value) => setFormData((prev) => ({ ...prev, [name]: value }));

  const handleConfirmDelete = async () => {
    if (!selectedRow) return;
    try {
      const body = { recordId: 0, customerNo: selectedRow?.customerNo || '' };
      const res = await apiService.delete('DELETE_CUSTOMER', body);
      if (res?.data?.isSuccessful && res?.data?.result) {
        toast.success(res?.data?.message || t('customerMaster.deleteSuccess'));
        rangeCache.current.key = '';
        setPreviewModal(false);
        setSelectedRow(null);
        getItems();
        refreshCounts();
      } else {
        toast.warning(res?.data?.message || t('customerMaster.deleteFailed'));
      }
    } catch (error) {
      console.error('Error deleting customer:', error);
      toast.error(t('customerMaster.deleteFailed'));
    }
  };

  const closeFormModal = () => setFormModal({ mode: null, data: null });
  const handleFormSaved = () => {
    closeFormModal();
    rangeCache.current.key = '';
    getItems(1);
    refreshCounts();
  };

  const visible = useMemo(() => {
    const q = quick.trim().toLowerCase();
    let list = rows.map((r) => ({ ...r, mobile: mobileOf(r) }));
    if (q) list = list.filter((r) => [r.customerNo, r.customerName, r.mobile, r.emailId, r.tradeLicNo, r.country, r.nationality, r.city, r.createdBy, r.unitNo].join(' ').toLowerCase().includes(q));
    if (sort.key) {
      list = [...list].sort((a, b) => String(a[sort.key] ?? '').localeCompare(String(b[sort.key] ?? ''), undefined, { numeric: true }) * sort.dir);
    }
    return list;
  }, [rows, quick, sort]);
  const toggleSort = (key, dir) => setSort((s) => (s.key === key && s.dir === dir ? { key: '', dir: 1 } : { key, dir }));

  const exportPDF = () => {
    if (!visible.length) {
      alert('No data available to export');
      return;
    }
    const doc = new jsPDF('landscape');
    const primaryColor = [110, 81, 153]; // #6E5199
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, 297, 25, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.text('CUSTOMER LIST REPORT', 148.5, 15, { align: 'center' });
    doc.setFontSize(9);
    doc.text(`Generated On: ${new Date().toLocaleDateString()}`, 280, 21, { align: 'right' });
    doc.setTextColor(0, 0, 0);
    const tableColumn = ['Contact ID', 'Name', 'Mobile No', 'Email', 'ID', 'Country', 'Nationality', 'City', 'Created On', 'Created By', 'Project'];
    const tableRows = visible.map((item) => [
      item.customerNo || '-', item.customerName || '-', item.mobile || '-', item.emailId || '-', item.tradeLicNo || '-',
      item.country || '-', item.nationality || '-', item.city || '-', dateText(item.createdOn) || '-', item.createdBy || '-', item.unitNo || '-',
    ]);
    doc.autoTable({
      startY: 35,
      head: [tableColumn],
      body: tableRows,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 3, valign: 'middle', lineColor: [220, 220, 220], lineWidth: 0.1 },
      headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center', fontSize: 9 },
      alternateRowStyles: { fillColor: [248, 246, 252] },
      bodyStyles: { textColor: [50, 50, 50] },
      margin: { left: 10, right: 10 },
      didDrawPage(data) {
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFontSize(8);
        doc.setTextColor(120);
        doc.text(`Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`, data.settings.margin.left, doc.internal.pageSize.height - 5);
        doc.text('Customer List', doc.internal.pageSize.width - 40, doc.internal.pageSize.height - 5);
      },
    });
    doc.save('Customer_List.pdf');
  };

  const periodLabel = range === 'all' ? 'All time' : `Last ${range} days`;
  const all = counts?.all ?? 0;
  const pct = (n) => (all ? Math.round((n / all) * 100) : 0);
  const total = pagination?.totalRecords ?? visible.length;
  const kindTitle = { all: t('customerMaster.allCustomers', 'All customers'), sale: t('customerMaster.filters.saleCustomer'), lease: t('customerMaster.filters.leaseCustomer'), prospect: t('customerMaster.filters.prospectLevel') };

  return (
    <div className="customer-list-page cl-page">
      <div className="cl-titlebar">
        <div className="cl-title">
          <h1>{t('customerMaster.pageTitle')}</h1>
          <p>Every tenant, buyer and lead in one place, from first enquiry to signed deal.</p>
        </div>
        <RangeChips value={range} onChange={(v) => { setRange(v); getItems(1, { range: v }); loadCounts(v); }} />
      </div>

      {counts && (
        <section className="cl-panel cl-cards" aria-label="Customer summary">
          <button type="button" className={`cl-card cl-card-main${kind === 'all' ? ' on' : ''}`} aria-pressed={kind === 'all'} onClick={() => pickKind('all')}>
            <div className="cl-tile-top"><span>Total customers</span><span className="cl-mono">{periodLabel}</span></div>
            <div className="cl-tile-big"><span className="cl-mono">{all.toLocaleString()}</span><em>{`${counts.sale + counts.lease} sale or lease`}</em></div>
          </button>
          {[
            ['sale', t('customerMaster.filters.saleCustomer'), '#1F5F7A', counts.sale],
            ['lease', t('customerMaster.filters.leaseCustomer'), '#4A2C86', counts.lease],
            ['prospect', t('customerMaster.filters.prospectLevel'), '#B07A1F', counts.prospect],
          ].map(([k, label, color, n]) => (
            <button type="button" key={k} className={`cl-card${kind === k ? ' on' : ''}`} aria-pressed={kind === k} onClick={() => pickKind(k)}>
              <div className="cl-tile-label"><i style={{ background: color }} />{label}</div>
              <div className="cl-tile-val"><span className="cl-mono">{n.toLocaleString()}</span><small>customers</small></div>
              <div className="cl-card-note cl-mono">{pct(n)}% of {range === 'all' ? 'total' : 'period'}</div>
            </button>
          ))}
        </section>
      )}

      <section className="cl-panel cl-filters" aria-label={t('common.searchParameters')}>
        <div className="form-group grid-contain">
          <div className="d-flex">
            <label className="form-label">{t('customerMaster.filters.contactId')}</label>
            <input type="text" className="p-2 form-control" name="contactid" value={formData.contactid} onChange={handleInputChange} onKeyDown={(e) => e.key === 'Enter' && getItems(1)} />
          </div>
          <div className="d-flex">
            <label className="form-label">{t('customerMaster.filters.contactNo')}</label>
            <input type="text" className="p-2 form-control" name="contactNo" value={formData.contactNo} onChange={handleInputChange} onKeyDown={(e) => e.key === 'Enter' && getItems(1)} />
          </div>
          <div className="d-flex">
            <label className="form-label">{t('customerMaster.filters.name')}</label>
            <input type="text" className="p-2 form-control" name="fullName" value={formData.fullName} onChange={handleInputChange} onKeyDown={(e) => e.key === 'Enter' && getItems(1)} />
          </div>
          <div className="d-flex">
            <label className="form-label">{t('customerMaster.filters.email')}</label>
            <input type="email" className="p-2 form-control" name="email" value={formData.email} onChange={handleInputChange} onKeyDown={(e) => e.key === 'Enter' && getItems(1)} />
          </div>
          <div className="d-flex">
            <label className="form-label">{t('customerMaster.filters.id')}</label>
            <input type="text" className="p-2 form-control" name="id" value={formData.id} onChange={handleInputChange} />
          </div>
          <div className="d-flex">
            <label className="form-label">{t('customerMaster.filters.type')}</label>
            <Select {...selectProps} value={formData.type} onChange={setSelect('type')} options={customerTypeList.map((c) => ({ value: c, label: c?.text }))} isClearable />
          </div>
          <div className="d-flex">
            <label className="form-label">{t('customerMaster.filters.country')}</label>
            <Select {...selectProps} value={formData.country} onChange={setSelect('country')} options={countriesList.map((c) => ({ value: c, label: c?.text }))} isClearable />
          </div>
          <div className="d-flex">
            <label className="form-label">{t('customerMaster.filters.nationality')}</label>
            <Select {...selectProps} value={formData.nationality} onChange={setSelect('nationality')} options={nationalityList.map((c) => ({ value: c, label: c?.text }))} isClearable />
          </div>
          <div className="d-flex">
            <label className="form-label">{t('customerMaster.filters.leadStatus')}</label>
            <Select {...selectProps} value={formData.leadStatus} onChange={setSelect('leadStatus')} options={leadStatus.map((c) => ({ value: c, label: c?.text }))} />
          </div>
          <div className="cl-chips" role="group" aria-label="Customer kind">
            {[['salecustomer', 'saleCustomer'], ['leaseCustomer', 'leaseCustomer'], ['prospectlevel', 'prospectLevel']].map(([key, label]) => (
              <label className="cl-chip" key={key}>
                <input type="checkbox" checked={!!formData[key]} onChange={(e) => setFormData((prev) => ({ ...prev, [key]: e.target.checked }))} />
                <span className="cl-ck" />
                {t(`customerMaster.filters.${label}`)}
              </label>
            ))}
          </div>
          <div className="cl-filter-actions">
            <button type="button" className="cl-btn cl-btn-ghost" onClick={handleClearFilters}><FiRotateCcw size={16} />{t('common.reset')}</button>
            <button type="button" className="cl-btn cl-btn-primary" onClick={() => getItems(1)}><FiSearch size={16} />{t('common.search')}</button>
          </div>
        </div>
      </section>

      <section className="cl-panel cl-list">
        <div className="cl-list-head">
          <div>
            <h2>{t('customerMaster.pageTitle')} List</h2>
            <div className="cl-sub">{kind === 'all' ? kindTitle.all : `Filtered: ${kindTitle[kind]}`}</div>
          </div>
          <div className="cl-head-actions">
            <div className="cl-quick">
              <FiSearch size={15} />
              <input value={quick} onChange={(e) => setQuick(e.target.value)} placeholder="Quick search in results" aria-label="Quick search in results" />
            </div>
            <button type="button" className="cl-btn cl-btn-ghost cl-btn-sm" onClick={exportPDF}><FiDownload size={16} />{t('common.export')}</button>
            <button type="button" className="cl-btn cl-btn-primary cl-btn-sm" onClick={() => setFormModal({ mode: 'create', data: null })}><FiPlus size={16} />Add customer</button>
          </div>
        </div>
        <div className="cl-table-wrap">
          <table className="cl-table" style={{ minWidth: 1400 }}>
            <thead>
              <tr>
                <th scope="col" className="cl-actions-h">{t('common.actions')}</th>
                {COLUMNS.map(([key, label]) => (
                  <th scope="col" key={key}>
                    <span className="cl-th">
                      {t(`customerMaster.columns.${label}`)}
                      <span className="cl-sort">
                        <button type="button" aria-label={`Sort ${label} ascending`} className={sort.key === key && sort.dir === 1 ? 'on' : ''} onClick={() => toggleSort(key, 1)}><FiArrowUp size={12} /></button>
                        <button type="button" aria-label={`Sort ${label} descending`} className={sort.key === key && sort.dir === -1 ? 'on' : ''} onClick={() => toggleSort(key, -1)}><FiArrowDown size={12} /></button>
                      </span>
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => {
                const [bg, fg] = AV_COLORS[(parseInt(r.customerNo, 10) || 0) % AV_COLORS.length];
                const name = blank(r.customerName) ? '' : r.customerName;
                return (
                  <tr key={r.customerNo || r.recordId}>
                    <td className="cl-actions">
                      <button type="button" className="cl-ico" title={t('common.view')} aria-label={t('common.view')} onClick={() => setFormModal({ mode: 'view', data: r })}><FiEye size={18} /></button>
                      <button type="button" className="cl-ico" title={t('common.edit')} aria-label={t('common.edit')} onClick={() => setFormModal({ mode: 'edit', data: r })}><FiEdit2 size={18} /></button>
                      <button
                        type="button"
                        className="cl-ico danger"
                        disabled={!isAdmin}
                        title={isAdmin ? t('common.delete') : t('common.onlyAdminCanDelete')}
                        aria-label={t('common.delete')}
                        onClick={() => { setSelectedRow(r); setPreviewModal(true); }}
                      ><FiTrash2 size={18} /></button>
                    </td>
                    <td><button type="button" className="cl-link" onClick={() => setFormModal({ mode: 'view', data: r })}>{blank(r.customerNo) ? '—' : r.customerNo}</button></td>
                    <td>
                      <span className="cl-who" title={name}>
                        <span className="cl-ini" style={{ background: bg, color: fg }}>{initialsOf(name)}</span>
                        <b>{name || dash}</b>
                      </span>
                    </td>
                    <td className="cl-mono cl-subtext">{r.mobile || dash}</td>
                    <td className="cl-clip" title={r.emailId}>{blank(r.emailId) ? dash : r.emailId}</td>
                    <td className="cl-mono cl-subtext">{blank(r.tradeLicNo) ? dash : r.tradeLicNo}</td>
                    <td>{blank(r.country) ? dash : r.country}</td>
                    <td>{blank(r.nationality) ? dash : r.nationality}</td>
                    <td>{blank(r.city) ? dash : r.city}</td>
                    <td className="cl-mono cl-subtext">{dateText(r.createdOn) || dash}</td>
                    <td className="cl-clip" title={r.createdBy}>{blank(r.createdBy) ? dash : r.createdBy}</td>
                    <td className="cl-mono cl-subtext">{blank(r.unitNo) ? dash : r.unitNo}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {loading && <div className="cl-empty">{t('common.loading')}</div>}
        {!loading && visible.length === 0 && <div className="cl-empty">No customers match these filters. Reset the filters to see the full list.</div>}
        <Pagination pageSize={pageSize} setPageSize={setPageSize} pagination={pagination} getItems={getItems} />
        <div className="cl-foot-note">Showing <span className="cl-mono">{visible.length}</span> of <span className="cl-mono">{total}</span> customers</div>
      </section>

      <Modal show={previewModal} size="md" onHide={() => setPreviewModal(false)} backdrop="static" keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title>{t('customerMaster.deleteTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Modal.Title>{t('customerMaster.deleteConfirm', { name: selectedRow?.customerName })}</Modal.Title>
        </Modal.Body>
        <Modal.Footer className="end p-3">
          <button className="mainbtnRed" onClick={() => { setPreviewModal(false); setSelectedRow(null); }}>{t('common.cancel')}</button>
          <button className="mainbtn" onClick={handleConfirmDelete}>{t('common.delete')}</button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!formModal.mode} onHide={closeFormModal} backdrop="static" keyboard={false} centered dialogClassName="customer-form-modal" contentClassName="p-0">
        <Modal.Body style={{ padding: 0 }}>
          {formModal.mode === 'create' && <CustomerMaster onClose={closeFormModal} onSaved={handleFormSaved} />}
          {(formModal.mode === 'edit' || formModal.mode === 'view') && (
            <UpdateCustomerMaster initialData={formModal.data} viewOnly={formModal.mode === 'view'} onClose={closeFormModal} onSaved={handleFormSaved} />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default CustomerListing;
